import { useCallback, useEffect, useState } from 'react';
import { v4 as uuid } from 'uuid';
import { getCapabilities, getCandidates, listAccounts } from '../../../../services/ReconciliationActionService';
import useInvoiceSettlement from './useInvoiceSettlement';

/**
 * Booking a bank row by hand through the reconciliation contract: prepare →
 * preview → execute as one immutable action, which gives the row its
 * history and undo. Works for money going out and money coming in
 * (book_from_proposal). A receipt (underlag) can be attached to an outgoing
 * amount, which makes it a book_direct_purchase. `applies` says whether the
 * contract is in force for the line; when it is not (older backend), the
 * caller books the legacy way. That legacy booking is a deprecated fallback:
 * remove it with the old bank writers once every tenant is migrated.
 *
 * With `payment` ({ side, invoiceId }) the same kontering registers the
 * payment of that invoice instead: on kontantmetoden the payment is what
 * books the cost, so the step is the same form with a different action.
 *
 * Rendering is the caller's: the editor shows the underlag field and the
 * preview, the footer drives `prepare` / `action.execute`.
 */
export default function usePurchaseAction({ companyId, line, rows, description, enabled = true, payment = null }) {
    // null = checking, true/false = known, 'unavailable' = could not check.
    const [supported, setSupported] = useState(null);
    const [receipts, setReceipts] = useState(false);
    const [doc, setDoc] = useState(null);
    const [error, setError] = useState('');
    const [pickerOpen, setPickerOpen] = useState(false);
    const action = useInvoiceSettlement({ companyId, line: line || { id: null }, storageNamespace: 'bank-purchase' });

    useEffect(() => {
        if (!enabled) return undefined;
        let active = true;
        setError('');
        getCapabilities(companyId)
            .then((c) => {
                if (!active) return;
                setSupported(!!c.actions?.includes('book_from_proposal'));
                setReceipts(!!c.actions?.includes('book_direct_purchase'));
            })
            .catch(() => { if (active) setSupported('unavailable'); });
        return () => { active = false; };
    }, [companyId, enabled]);

    const clearDoc = useCallback(() => setDoc(null), []);

    const amount = Number(line?.amount);
    const bookable = enabled && !!line && Number.isFinite(amount) && amount !== 0;
    const applies = bookable && supported === true;
    const checking = bookable && supported === null;
    // A receipt backs a purchase, so only an outgoing amount can carry one.
    // An invoice carries its own underlag.
    const canAttachReceipt = applies && receipts && amount < 0 && !payment;
    const receipt = canAttachReceipt ? doc : null;

    const prepare = useCallback(async () => {
        setError('');
        try {
            const [facts, accounts] = await Promise.all([getCandidates(companyId, line.id), listAccounts(companyId)]);
            const accountFor = (r) => accounts.find((a) => String(a.account_number) === String(r.account_number));
            // The rows ARE the VAT handling; the treatment is a declaration the
            // backend checks the rows against (foreign receipt ⇒ no VAT
            // accounts, exempt tenant ⇒ no input VAT). Derive it rather than
            // ask: a VAT-mapped account on any row means Swedish VAT applies.
            const vat_treatment = rows.some((r) => String(accountFor(r)?.VAT_mapping || '').trim()) ? 'taxable' : 'out_of_scope';
            const journal_rows = rows.map((r) => ({
                account_id: Number(accountFor(r)?.account_id),
                account_number: String(r.account_number),
                debit_ore: Math.round(Number(r.debit || 0) * 100),
                credit_ore: Math.round(Number(r.credit || 0) * 100),
                vat_treatment,
            }));
            const request = payment
                ? { type: `register_${payment.side}_payment`, target: { invoice_id: payment.invoiceId } }
                : receipt
                    ? { type: 'book_direct_purchase', target: { document_ids: [receipt.document_id] } }
                    : { type: 'book_from_proposal', target: { proposal_id: uuid(), provenance: 'manual', precedent_verification_ids: [], rule_id: null } };
            await action.prepareRequest({
                ...request,
                expected_bank_generation: facts.bank.generation,
                expected_bank_revision: facts.bank.revision,
                evidence_ids: [],
                accounting_inputs: {
                    booking_date: String(line.booking_date || line.transaction_date).slice(0, 10),
                    series: 'V',
                    template_id: null,
                    template_version: null,
                    business_purpose: description,
                    journal_rows,
                },
            });
        } catch (e) {
            setError(e.response?.data?.message || 'Kunde inte förhandsgranska.');
        }
    }, [companyId, line, rows, receipt, description, action, payment]);

    return {
        applies, checking, unavailable: supported === 'unavailable',
        canAttachReceipt, doc: receipt, setDoc, clearDoc,
        pickerOpen, setPickerOpen,
        error: error || action.error,
        action, prepare,
    };
}
